/**
 * Control Room › Leverandører (Atle 03.11, `docs/LEVERANDORER-I-CONTROL-ROOM.md`):
 * `GET /v1/platform/vendors` og `POST /v1/platform/vendors/refresh`.
 *
 * Én seksjon per leverandør, med tallene som rader (brukt, grense, igjen). Leverandørene kalles av
 * API-ets henter på en timer og ved første visning, aldri når siden åpnes; Soundcharts kalles aldri
 * herfra (det lagrede leses). `own` er våre egne tall fra basen.
 *
 * Bare tall, koder og navn på miljøvariabler går ut — aldri en nøkkel, en e-postadresse eller en
 * feiltekst fra leverandøren.
 */
import { z } from 'zod';

export const PLATFORM_VENDOR_IDS = [
  'soundcharts',
  'tavily',
  'brevo_email',
  'brevo_sms',
  'supabase',
  'cloudflare',
  'railway',
  'own',
] as const;
export type PlatformVendorId = (typeof PLATFORM_VENDOR_IDS)[number];

export const PLATFORM_VENDOR_METRIC_KEYS = [
  'soundcharts_quota',
  'soundcharts_calls_today',
  'soundcharts_calls_month',
  'soundcharts_unlocks_month',
  'tavily_plan_credits',
  'tavily_paygo_credits',
  'tavily_key_credits',
  'tavily_search',
  'tavily_extract',
  'tavily_map',
  'tavily_crawl',
  'tavily_research',
  'brevo_email_credits',
  'brevo_sms_credits',
  'supabase_disk',
  'supabase_auth_requests',
  'supabase_rest_requests',
  'supabase_storage_requests',
  'supabase_realtime_requests',
  'r2_storage',
  'r2_objects',
  'r2_class_a',
  'r2_class_b',
  'pages_builds',
  'worker_requests',
  'railway_cost',
  'railway_estimate',
  'railway_credit',
  'database_size',
  'attachment_storage',
  'active_users',
  'sms_sent_month',
  'sms_sent_last_month',
] as const;
export type PlatformVendorMetricKey = (typeof PLATFORM_VENDOR_METRIC_KEYS)[number];

export const PLATFORM_VENDOR_UNITS = ['count', 'bytes', 'usd', 'credits', 'requests'] as const;
export type PlatformVendorUnit = (typeof PLATFORM_VENDOR_UNITS)[number];

export const PLATFORM_VENDOR_PERIODS = [
  'now',
  'day',
  'month',
  'last_month',
  'billing_period',
  'last_7_days',
  'last_30_days',
] as const;
export type PlatformVendorPeriod = (typeof PLATFORM_VENDOR_PERIODS)[number];

/**
 * Det siden må si for at et tall skal leses riktig. `soundcharts_stored`: lest fra basen, Soundcharts
 * er ikke spurt. `mau_from_activity`: `auth.users` er ikke lesbar for API-et, så «aktive brukere» er
 * Tvitvis egne aktivitetsdager. `email_last_month_missing`: forrige måned finnes bare for SMS.
 * `estimate_linear`: anslaget er dagens forbruk regnet ut over perioden. `storage_excludes_personal`:
 * personlige kontoer er ikke med i lagringssummen. `supabase_url_not_hosted`: `SUPABASE_URL` peker
 * ikke på et prosjekt hos Supabase (lokalt).
 */
export const PLATFORM_VENDOR_NOTES = [
  'soundcharts_stored',
  'mau_from_activity',
  'email_last_month_missing',
  'estimate_linear',
  'storage_excludes_personal',
  'supabase_url_not_hosted',
] as const;
export type PlatformVendorNote = (typeof PLATFORM_VENDOR_NOTES)[number];

/** En maskinlesbar kode, aldri en setning og aldri en vert eller en nøkkel. */
const codeSchema = z
  .string()
  .max(40)
  .regex(/^[a-z0-9_]+$/);

export const platformVendorMetricSchema = z
  .object({
    key: z.enum(PLATFORM_VENDOR_METRIC_KEYS),
    used: z.number().nonnegative().nullable(),
    limit: z.number().nonnegative().nullable(),
    /** Grense minus brukt når begge finnes, eller det leverandøren sier er igjen (Brevo, Railway). */
    remaining: z.number().nullable(),
    unit: z.enum(PLATFORM_VENDOR_UNITS),
    period: z.enum(PLATFORM_VENDOR_PERIODS).nullable(),
    periodEnd: z.iso.datetime().nullable(),
    /** `vendor`: grensen kom fra leverandøren. `plan`: fra plantabellen i API-et. `own`: vår egen innstilling. */
    limitSource: z.enum(['vendor', 'plan', 'own']).nullable(),
  })
  .meta({ id: 'PlatformVendorMetric' });

export const platformVendorSectionSchema = z
  .object({
    vendor: z.enum(PLATFORM_VENDOR_IDS),
    /** `pending`: satt opp, men første henting er ikke ferdig ennå. */
    status: z.enum(['ok', 'not_configured', 'error', 'pending']),
    /** Hvorfor siste henting feilet: `timeout`, `network`, `unauthorized`, `rate_limited`, `provider_503` … */
    errorCode: codeSchema.nullable(),
    /** Navnene på miljøvariablene som mangler. Aldri en verdi. */
    missingEnv: z.array(
      z
        .string()
        .max(60)
        .regex(/^[A-Z0-9_]+$/),
    ),
    plan: z.string().max(60).nullable(),
    metrics: z.array(platformVendorMetricSchema),
    /** Tall som ble prøvd hentet, men ikke kom (en del av svaret feilet). */
    unavailable: z.array(z.enum(PLATFORM_VENDOR_METRIC_KEYS)),
    /** Delsjekker leverandøren svarer på (Supabase: db, auth, rest …). */
    checks: z.array(z.object({ name: codeSchema, ok: z.boolean() })),
    notes: z.array(z.enum(PLATFORM_VENDOR_NOTES)),
    /** Når tallene i `metrics` ble hentet. Ved en feil står de forrige tallene med sin tid. */
    fetchedAt: z.iso.datetime().nullable(),
    /** Siste forsøk, vellykket eller ikke. */
    attemptedAt: z.iso.datetime().nullable(),
    billingUrl: z.url().nullable(),
  })
  .meta({ id: 'PlatformVendorSection' });

/** Statusene `message_deliveries` kan ha (0491). */
export const PLATFORM_VENDOR_MESSAGE_STATUSES = [
  'queued',
  'sent',
  'delivered',
  'bounced',
  'blocked',
  'failed',
  'cancelled',
] as const;

export const platformVendorMessageCountSchema = z
  .object({
    channel: z.enum(['email', 'sms']),
    status: z.enum(PLATFORM_VENDOR_MESSAGE_STATUSES),
    /** Laget denne måneden (Oslo-tid), hele plattformen. */
    thisMonth: z.number().int().nonnegative(),
  })
  .meta({ id: 'PlatformVendorMessageCount' });

export const platformVendorsResponseSchema = z
  .object({
    sections: z.array(platformVendorSectionSchema),
    messages: z.array(platformVendorMessageCountSchema),
    refresh: z.object({
      /** Henteren går nå; siden spør igjen om litt. */
      running: z.boolean(),
      lastManualAt: z.iso.datetime().nullable(),
      /** «Hent nå» er åpen igjen fra dette tidspunktet (én gang i minuttet). */
      nextManualAt: z.iso.datetime().nullable(),
    }),
  })
  .meta({ id: 'PlatformVendorsResponse' });

export type PlatformVendorMetric = z.infer<typeof platformVendorMetricSchema>;
export type PlatformVendorSection = z.infer<typeof platformVendorSectionSchema>;
export type PlatformVendorMessageCount = z.infer<typeof platformVendorMessageCountSchema>;
export type PlatformVendorsResponse = z.infer<typeof platformVendorsResponseSchema>;
